// Feature P9 — small things that carry the vibe. Pure rules, numbers and copy;
// the sim (sim.ts, mower.ts, tools/*), the board (contract.ts) and the shell
// read them. Every rule here is opt-in (SimOptions / BoardOptions / FEATURES),
// so a bare createSim or generateBoard is exactly what it was.
//
// Imports stay at rng + types: contract → spawn → vibe must not loop back.

import type { Contract, GroundItem, GroundObject, JobResult, Layout, LocationGroup } from './types';
import { mixSeed } from './rng';

// ─── Named ground-check items ───────────────────────────────────────────

/** What a ground check turns up (SimOptions.groundItems). */
export const GROUND_ITEMS: readonly GroundItem[] = ['stick', 'newspaper', 'thong', 'cricket-ball', 'footy', 'wallet', 'dog-toy', 'sunnies'];

/** The GroundObject kind each item rides on (renderers draw by kind; thong and sunnies borrow a look). */
export const GROUND_ITEM_KIND: Readonly<Record<GroundItem, GroundObject['kind']>> = {
  stick: 'stick', newspaper: 'newspaper', thong: 'frisbee', 'cricket-ball': 'cricket-ball', footy: 'footy',
  wallet: 'wallet', 'dog-toy': 'dog-toy', sunnies: 'school-hat',
};

const ITEM_NAME: Readonly<Record<GroundItem, string>> = {
  stick: 'stick', newspaper: 'newspaper', thong: 'thong', 'cricket-ball': 'cricket ball', footy: 'footy',
  wallet: 'wallet', 'dog-toy': 'dog toy', sunnies: 'sunnies',
};

/** Display name: the named item, else the kind ("cricket ball"). */
export function groundName(o: Pick<GroundObject, 'kind' | 'item'>): string {
  return o.item ? ITEM_NAME[o.item] : o.kind.replace(/-/g, ' ');
}

/** A handed-back wallet pays this, its own "honest bloke" line. */
export const HONEST_BLOKE_AUD = 5;
export const HONEST_BLOKE_COPY = 'honest bloke';

export function isWallet(o: Pick<GroundObject, 'kind' | 'item'>): boolean {
  return (o.item ?? o.kind) === 'wallet';
}

/** Toast when an item is picked up. */
export function collectCopy(o: Pick<GroundObject, 'kind' | 'item'>): string {
  if (isWallet(o)) return 'Found a wallet. Back to the owner — honest bloke.';
  if (o.item === 'sunnies') return 'Found some sunnies.';
  return `Found a ${groundName(o)}.`;
}

/** Wallets collected (not mulched, not nicked by an ibis) → the honest-bloke bonus. */
export function honestBloke(objects: readonly GroundObject[]): { wallets: number; aud: number } {
  let wallets = 0;
  for (const o of objects) if (o.state === 'collected' && isWallet(o)) wallets++;
  return { wallets, aud: wallets * HONEST_BLOKE_AUD };
}

// ─── Extra mile ─────────────────────────────────────────────────────────

/** One-off goodwill for work beyond the contract. Never more than once a job. */
export const EXTRA_MILE_AUD = 10;
export const EXTRA_MILE_REP = 1;
/** Share of the yard's trim ring + weeds snipped, on a job with no trim task, that counts. */
export const EXTRA_MILE_TRIM_SHARE = 0.5;
export const EXTRA_MILE_COPY = 'Extra mile · trimmed when not asked';

export interface ExtraMileInput {
  abandoned: boolean;
  /** The contract has a trim task (then trimming is the job, not extra). */
  trimTask: boolean;
  /** Trim-ring cells the trimmer cut and weeds it cleared. */
  trimmed: number;
  weedsCleared: number;
  /** Trim-ring cells and weeds the yard started with. */
  trimCells: number;
  weedCells: number;
}

export function extraMileEarned(x: ExtraMileInput): boolean {
  if (x.abandoned || x.trimTask) return false;
  const den = x.trimCells + x.weedCells;
  return den > 0 && (x.trimmed + x.weedsCleared) / den >= EXTRA_MILE_TRIM_SHARE - 1e-9;
}

/** P9 money on top of fee + tip (+ stripe bonus): the honest bloke and the extra mile. */
export function bonusAud(r: Pick<JobResult, 'honestBlokeAud' | 'extraMileAud'>): number {
  return (r.honestBlokeAud ?? 0) + (r.extraMileAud ?? 0);
}

// ─── Slope drift ────────────────────────────────────────────────────────

/** Seconds: drift m/s at full speed = |slope| × gameScale × this. */
export const SLOPE_DRIFT_GAIN = 1;

/** Downhill drift (m) for one step at `speedFrac` (0..1 of top speed). Zero when stopped. */
export function slopeDrift(slope: Layout['slope'], speedFrac: number, gs: number, dt: number): [number, number] {
  if (!slope) return [0, 0];
  const f = Math.min(1, Math.max(0, speedFrac)) * gs * SLOPE_DRIFT_GAIN * dt;
  return [slope.x * f, slope.y * f];
}

// ─── Trimmer ────────────────────────────────────────────────────────────

/** The snipper's line cuts to about this, well under any lawn's range (SimOptions.trimOvercut). */
export const TRIM_OVERCUT_MM = 15;

/**
 * Where the trimmer leaves a grass cell: the range midpoint on the trim ring
 * (and with over-cut off), else the lower of that and TRIM_OVERCUT_MM on
 * mowable cells: snipping the open lawn scalps it.
 */
export function trimTargetMm(mid: number, mowable: boolean, overcut: boolean): number {
  return overcut && mowable ? Math.min(mid, TRIM_OVERCUT_MM) : mid;
}

/** Blow-back: each clipped paved cell knocks this off the clear task, up to the cap. */
export const BLOWBACK_PCT_PER_CELL = 0.5;
export const BLOWBACK_MAX_PCT = 5;

export function blowBackNudge(clippedCells: number): number {
  return Math.min(BLOWBACK_MAX_PCT, Math.max(0, clippedCells) * BLOWBACK_PCT_PER_CELL);
}

// ─── Zero-turn, bumps, tyre tracks ──────────────────────────────────────

/** Stick steering: a zero-turn asked to turn more than this pivots on the spot. */
export const ZT_PIVOT_ANGLE = Math.PI / 3;
/** Rear-swing circle while pivoting: centre this × mower radius behind, radius this × mower radius. */
export const ZT_REAR_OFFSET = 0.9;
export const ZT_REAR_R = 0.7;
export const ZT_TIP = 'Zero-turn: it spins on the spot, but the back end swings wide.';

/** Rolling over a passable prop keeps this share of the speed (the bump). */
export const BUMP_SPEED_MULT = 0.8;
/** Loose stone/mulch edging throws onto at most this many nearby cells per pass. */
export const SCATTER_CELLS = 4;

/** Matted wheel tracks fade out over this many seconds (in FADE_STEPS steps). */
export const TYRE_TRACK_FADE_S = 30;
export const TYRE_TRACK_FADE_STEPS = 6;

/** grid.matted level for a track this old: 255 fresh, stepping down to 0 at TYRE_TRACK_FADE_S. */
export function trackLevel(ageS: number): number {
  if (!(ageS < TYRE_TRACK_FADE_S)) return 0;
  const k = Math.ceil((1 - Math.max(0, ageS) / TYRE_TRACK_FADE_S) * TYRE_TRACK_FADE_STEPS);
  return Math.round((255 * k) / TYRE_TRACK_FADE_STEPS);
}

// ─── Neglected grass ────────────────────────────────────────────────────

export const NEGLECTED_FEE_MULT = 1.3;
export const NEGLECTED_LENGTH_MULT = 2;
/** Doubled start lengths stop here (a hobby-farm paddock doesn't become half a metre). */
export const NEGLECTED_LENGTH_CAP_MM = 240;
/** Mower top speed while it's in the long stuff. */
export const NEGLECTED_SPEED_MULT = 0.8;
/** Share of eligible contracts that come up neglected. */
export const NEGLECTED_CHANCE = 0.2;
export const NEGLECTED_COPY = "Hasn't been mowed since Easter.";
/** Kept yards only: ovals and clubs have a curator, rural blocks are always long. */
export const NEGLECTED_GROUPS: readonly LocationGroup[] = ['backyards', 'nature-strips', 'parks'];
const NEGLECT_SALT = 0xea57e5;

/** The layout a neglected contract is mowed on: uncut lengths doubled (capped). */
export function neglectedLayout<T extends Layout>(layout: T): T {
  const [lo, hi] = layout.startLengthMm;
  const cap = NEGLECTED_LENGTH_CAP_MM;
  return {
    ...layout,
    startLengthMm: [Math.min(cap, lo * NEGLECTED_LENGTH_MULT), Math.min(cap, hi * NEGLECTED_LENGTH_MULT)],
  };
}

/** Is this a neglected yard? Seeded by the contract; only kept yards with a mow task. */
export function isNeglectedDraw(c: Pick<Contract, 'seed' | 'tasks'>, layout: Pick<Layout, 'locationGroup'>): boolean {
  if (!NEGLECTED_GROUPS.includes(layout.locationGroup) || !c.tasks.some((t) => t.kind === 'mow')) return false;
  return mixSeed(c.seed >>> 0, NEGLECT_SALT) / 4294967296 < NEGLECTED_CHANCE;
}

/** Board post-pass (BoardOptions.neglected): mark the draw and put the fee up ×1.3. */
export function withNeglect(c: Contract, layout: Pick<Layout, 'locationGroup'>): Contract {
  if (c.neglected || !isNeglectedDraw(c, layout)) return c;
  return { ...c, neglected: true, feeAud: Math.round(c.feeAud * NEGLECTED_FEE_MULT) };
}

// ─── Contract board tips ────────────────────────────────────────────────

export const BOARD_TIPS: readonly string[] = [
  'Mow first, trim second, blow last.',
  'Trim a box round every post before you bring the mower in.',
  "Walk the yard before you start. Wallets don't mulch well.",
  'Wet grass is heavy going. Raise the deck and take it slow.',
  'Headland lap first, then straight lines. The neighbours notice.',
  "Blow the paths last, or you'll be blowing them twice.",
  'Top of the range is the gentle cut. Kikuyu will thank you.',
  'A catcher saves a raking. A full tank saves a walk.',
  "Low edging you can roll over. The Hills hoist you can't.",
  "Keep the snipper off the open lawn. It cuts lower than the deck.",
  'Owner only asked for a mow? Doing the edges anyway goes a long way.',
  'Magpie season: head down, keep mowing.',
];

/** The tip for rotation step `n` (the shell uses today's day number + jobs done). */
export function boardTipFor(n: number): string {
  const k = BOARD_TIPS.length;
  return BOARD_TIPS[((Math.floor(n) % k) + k) % k];
}
